import { Stack, IconButton, Button, Alert, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { Control, useFieldArray, UseFormGetValues } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { useCreateOrUpdatePricingCalculatorMutation } from '../queries';
import SelectField from '@shared/components/SelectField';
import TextField from '@shared/components/TextField';
import { FinancialOutcome } from '@pages/price-calculator';
import { useCurrency } from '@features/currency/currency.hooks';

type FinancialCostsFormProps = {
  control: Control<FinancialOutcome>;
  month: Date;
  getValues: UseFormGetValues<FinancialOutcome>;
  lastUpdate: Date | undefined;
  onCopyFromPreviousMonth?: () => void;
  hasPreviousMonthData?: boolean;
};

const FinancialCostsForm = (props: FinancialCostsFormProps) => {
  const intl = useIntl();
  const { preferredCurrency } = useCurrency();
  const { control, month, getValues, lastUpdate, onCopyFromPreviousMonth, hasPreviousMonthData } =
    props;
  const { mutate: saveCalculator, isPending: isSaving } =
    useCreateOrUpdatePricingCalculatorMutation();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'costs',
  });

  const handleAddCost = () => {
    append({ name: '', amount: 0, period: 'monthly', description: '' });
  };

  const handleSaveCalculator = () => {
    saveCalculator({
      overheadCosts: getValues('costs'),
      calculationMonth: month,
    });
  };

  return (
    <Stack spacing={1}>
      {fields.map((field, index) => (
        <Stack
          key={field.id}
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            p: 1,
            borderRadius: 1,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <TextField
            label={intl.formatMessage({
              id: 'pricing.costs.form.name',
              defaultMessage: 'Název',
            })}
            control={control}
            fieldPath={`costs.${index}.name`}
            size="small"
          />
          <TextField
            control={control}
            label={intl.formatMessage({
              id: 'pricing.costs.form.amount',
              defaultMessage: preferredCurrency,
            })}
            size="small"
            type="number"
            fieldPath={`costs.${index}.amount`}
          />

          <SelectField
            keyExtractor={u => u.id}
            labelExtractor={u => u.name}
            fieldPath={`costs.${index}.period`}
            label={intl.formatMessage({
              id: 'pricing.costs.form.period',
              defaultMessage: 'Období',
            })}
            control={control}
            size="small"
            items={[
              {
                id: 'monthly',
                name: intl.formatMessage({
                  id: 'pricing.period.monthly',
                  defaultMessage: 'Měsíční',
                }),
              },
              {
                id: 'quarterly',
                name: intl.formatMessage({
                  id: 'pricing.period.quarterly',
                  defaultMessage: 'Čtvrtletní',
                }),
              },
              {
                id: 'yearly',
                name: intl.formatMessage({
                  id: 'pricing.period.yearly',
                  defaultMessage: 'Roční',
                }),
              },
            ]}
          />
          <IconButton onClick={() => remove(index)} color="error" size="small">
            <DeleteIcon />
          </IconButton>
        </Stack>
      ))}
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddCost}>
          <FormattedMessage id="pricing.costs.add" defaultMessage="Přidat náklad" />
        </Button>
        {hasPreviousMonthData && onCopyFromPreviousMonth && (
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={onCopyFromPreviousMonth}
            color="primary"
          >
            <FormattedMessage
              id="pricing.costs.copyFromPrevious"
              defaultMessage="Zkopírovat z minulého měsíce"
            />
          </Button>
        )}
      </Stack>
      <Stack spacing={2} direction="row" justifyContent="space-between" alignItems="center" mt={3}>
        <Button
          variant="contained"
          color="success"
          startIcon={<SaveIcon />}
          onClick={handleSaveCalculator}
          disabled={isSaving}
        >
          <FormattedMessage id="pricing.save" defaultMessage="Vypočítat a uložit" />
        </Button>
        {lastUpdate && (
          <Alert severity="error" sx={{ p: 0, bgcolor: 'transparent' }}>
            <Stack direction="column" justifyContent="center" spacing={0}>
              <Typography fontSize="0.6rem">
                <FormattedMessage id="pricing.lastSaved" defaultMessage="Poslední uložení" />
              </Typography>
              <Typography fontSize="0.6rem">
                {dayjs(lastUpdate).format('DD.MM.YYYY HH:mm:ss')}
              </Typography>
            </Stack>
          </Alert>
        )}
      </Stack>
    </Stack>
  );
};

export default FinancialCostsForm;
